import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { CategorySelectModalComponent, CategorySelectModalItem } from '../entry/category-select-modal';

export type HgmSelectItem = CategorySelectModalItem;

@Component({
    selector: 'hgm-select',
    imports: [IonicModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
            .main {
                display: flex;
                flex-direction: row;
                cursor: pointer;
                margin: var(--margin);
            }

            .main.disabled {
                opacity: 0.5;
            }

            .label {
                color: var(--ion-text-color);
                flex-grow: 1;
                text-align: var(--label-text-align, end);
            }

            .label.disabled {
                opacity: 0.8;
            }

            .icon {
                color: var(--ion-text-color);
                height: 19px;
                min-width: 19px;
                width: 19px;
                opacity: 0.33;
                position: relative;
            }

            .icon.disabled {
                visibility: hidden;
            }

            .icon-inner {
                left: 5px;
                top: 50%;
                margin-top: -3px;
                position: absolute;
                width: 0px;
                height: 0px;
                pointer-events: none;
                border-top: 5px solid;
                border-right: 5px solid transparent;
                border-left: 5px solid transparent;
            }

            .cover {
                left: 0px;
                top: 0px;
                margin-left: 0px;
                margin-right: 0px;
                margin-top: 0px;
                margin-bottom: 0px;
                position: absolute;
                width: 100%;
                height: 100%;
                cursor: pointer;
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
                border-width: 0px;
                border-style: initial;
                border-color: initial;
                border-image: initial;
                background: transparent;
                outline: none;
            }

            .disabled .cover {
                cursor: initial;
            }
        `,
    ],
    template: `
        @let dis = disabled();
        @let hasChoiceValue = hasChoice();
        @let currentItem = item();

        <div class="main" [class.disabled]="dis" (click)="onClicked($event)">
            <div class="label" [class.disabled]="!hasChoiceValue">{{ currentItem?.name || '' }}</div>
            <div class="icon" [class.disabled]="!hasChoiceValue || dis">
                <div class="icon-inner"></div>
            </div>
            @if (itemCover()) {
                <div class="cover" (click)="onClicked($event)"></div>
            }
        </div>
    `,
})
export class HgmSelectComponent {
    readonly items = input.required<HgmSelectItem[]>();
    readonly label = input.required<string>();
    readonly value = input.required<any>();
    readonly canSearch = input<boolean | undefined>(undefined);
    readonly disabled = input(false);
    readonly itemCover = input(true);
    readonly popoverClass = input<string | undefined>(undefined);
    readonly valueChange = output<any>();

    readonly item = computed(() => this.items().find((item) => item.key === this.value()));

    readonly hasChoice = computed(() => !this.item() || this.items().length > 1);

    private modalCtrlr = inject(ModalController);

    async onClicked(event: MouseEvent) {
        if (this.disabled() || !this.hasChoice()) {
            return;
        }
        event.stopPropagation();
        const modal = await this.modalCtrlr.create({
            component: CategorySelectModalComponent,
            componentProps: {
                title: this.label(),
                choices: [
                    {
                        name: '',
                        items: this.items(),
                        expanded: true,
                    },
                ],
                dismissOnClick: true,
                initialValue: this.value(),
                canSearch: this.canSearch(),
            },
            cssClass: this.popoverClass(),
            id: 'CategorySelectModalComponent',
        });
        await modal.present();
        const detail = await modal.onWillDismiss();
        if (detail.data !== undefined) {
            this.valueChange.emit(detail.data);
        }
    }
}
